/** Shared form styling for the Media Plans / Rate Cards tabs (mirrors TentpoleCalendar/EntryEditor). */

import React from 'react';

export const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13.5,
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  color: 'var(--fg-1)',
  background: '#fff',
};

export const numberFieldStyle: React.CSSProperties = {
  ...fieldStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

export const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  display: 'block',
  marginBottom: 4,
};

export const hintStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--fg-4)', marginTop: 6 };

export const errorTextStyle: React.CSSProperties = { fontSize: 12, color: 'var(--danger)', marginTop: 6, fontWeight: 600 };

export const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
};

export const sectionCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

export const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 14,
};

export const bannerStyle = (tone: 'info' | 'warning' | 'danger' | 'success'): React.CSSProperties => ({
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  background: `var(--${tone}-soft)`,
  color: `var(--${tone})`,
  fontSize: 13,
  fontWeight: 600,
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  flexWrap: 'wrap',
});

export const mutedText: React.CSSProperties = { fontSize: 12.5, color: 'var(--fg-4)' };

export const monoNum: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

export const fmtUsd = (n: number, dp = 2): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
export const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-US');
/**
 * A budget as the team writes it: "$250K", "$12.5K", "$1.25M" — but only when
 * the short form is the exact amount; otherwise "$12,345" (or cents). Same
 * rule as the engine's tier labels (compactUsd in mediaPlanEngine.ts).
 */
export const fmtBudget = (usd: number): string => {
  const cents = Math.round(usd * 100);
  if (cents % 100 !== 0) return fmtUsd(cents / 100);
  const dollars = cents / 100;
  const trim = (n: number) => n.toFixed(2).replace(/\.?0+$/, '');
  if (dollars >= 1_000_000 && dollars % 10_000 === 0) return `$${trim(dollars / 1_000_000)}M`;
  if (dollars >= 1_000 && dollars % 10 === 0) return `$${trim(dollars / 1_000)}K`;
  return fmtUsd(dollars, 0);
};
export const fmtDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: iso.length === 10 ? 'UTC' : undefined });
};
export const fmtPct = (n: number, dp = 0): string => `${n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: Math.max(dp, 1) })}%`;
/** A ratio change as a signed percent: −0.12 → "−12%", 0.081 → "+8%". */
export const fmtSignedPct = (ratio: number): string => {
  const pct = Math.round(ratio * 100);
  return pct === 0 ? '0%' : `${pct < 0 ? '−' : '+'}${Math.abs(pct)}%`;
};
export const fmtDateTime = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
